/**
 * companyTime.ts — Zeitrechnung in der Zeitzone des Studios, nicht des Browsers.
 *
 * WARUM DIESE DATEI EXISTIERT
 * ---------------------------
 * Kalendertermine liegen in der Datenbank als UTC (`SET time_zone='+00:00'` in
 * beiden Pools, server.js). `convertDateForIos()` im Store macht daraus ein
 * korrektes `Date` — also einen korrekten ZEITPUNKT. Angezeigt wurde dieser
 * Zeitpunkt bisher mit `getHours()` / `getDate()` / `formatDate()`, und die
 * lesen alle die Zeitzone des BROWSERS. Solange Studio und Betrachter beide in
 * Deutschland sitzen, stimmt das zufällig. Sobald ein Trainer aus Dubai auf den
 * Kalender schaut oder ein Studio nicht in Berlin liegt, zeigt derselbe Termin
 * zwei verschiedene Uhrzeiten — und der Termin um 06:00 rutscht auf den Vortag.
 *
 * Hier steht deshalb die eine Regel: Ein `Date` ist ein Zeitpunkt. Wandzeit
 * entsteht erst durch eine Zeitzone, und die Zeitzone ist die des Studios.
 *
 * WARUM NICHT DAS DATE VERSCHIEBEN
 * --------------------------------
 * Der billigere Weg wäre, das `Date` beim Lesen so zu verschieben, dass
 * `getHours()` schon Company-Wandzeit liefert. Das war bewusst NICHT die Wahl:
 * `getTimezoneOffset()` liefert danach weiterhin die Browser-Zone, und genau
 * darauf bauen die DST-Korrekturen in den Manager-Views auf ("wenn der Offset
 * zwischen Tagesbeginn und Terminbeginn springt, ±60 Minuten"). Ein
 * verschobenes Date hätte diese Korrekturen an den falschen Tagen feuern
 * lassen — deutschem DST-Datum folgend, obwohl das Studio in Sydney steht.
 * Die Korrekturen entfallen hier ersatzlos: `czMinutesOfDay()` liest die
 * Wandzeit direkt, und Wandzeit kennt keinen DST-Sprung.
 */

const MONTHNAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
const DAYNAMES   = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

export const DEFAULT_TIMEZONE = 'Europe/Berlin'

export interface CompanyDateParts {
  year: number
  month: number   // 1-12
  day: number     // 1-31
  hour: number    // 0-23
  minute: number
  second: number
  weekday: number // 0 = Sonntag … 6 = Samstag, wie Date.getDay()
}

/**
 * `Intl.DateTimeFormat` ist teuer im Aufbau und billig im Aufruf. Der Kalender
 * formatiert pro Renderdurchlauf schnell vierstellig viele Zeitpunkte — ohne
 * Cache baut er dabei ebenso viele Formatter. Deshalb einer pro Zeitzone.
 */
const partsFormatterCache = new Map<string, Intl.DateTimeFormat>()

function partsFormatter (timezone: string): Intl.DateTimeFormat {
  let fmt = partsFormatterCache.get(timezone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      weekday: 'short'
    })
    partsFormatterCache.set(timezone, fmt)
  }
  return fmt
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6
}

/**
 * Prüft eine IANA-Kennung, ohne bei einer unbekannten zu werfen.
 *
 * Das Ergebnis wird gecacht: der Probe-Konstruktor `new Intl.DateTimeFormat`
 * ist teuer, und czParts() validiert bei JEDEM Aufruf — ohne Cache baute der
 * Kalender pro gecachtem Event einen Wegwerf-Formatter und umging damit den
 * partsFormatterCache vollständig (auf einem iPad 8 sekundenweise Parse-Zeit).
 */
const timezoneValidityCache = new Map<string, boolean>()

export function isValidTimezone (timezone: unknown): timezone is string {
  if (typeof timezone !== 'string' || timezone === '') return false
  let valid = timezoneValidityCache.get(timezone)
  if (valid === undefined) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone })
      valid = true
    } catch {
      valid = false
    }
    timezoneValidityCache.set(timezone, valid)
  }
  return valid
}

/** Zeitzone des Browsers — für den Hinweis „du siehst gerade Studiozeit". */
export function browserTimezone (): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

/**
 * Wandzeit-Bestandteile eines Zeitpunkts in der Studiozeitzone.
 * Das ist die einzige Stelle, die `Intl` befragt; alles Weitere rechnet darauf.
 */
export function czParts (date: Date | string | number | null | undefined, timezone: string): CompanyDateParts | null {
  // null/undefined VOR dem Date-Konstruktor abfangen. `new Date(null)` ist
  // nicht Invalid Date, sondern der 01.01.1970 — die NaN-Prüfung darunter
  // greift also nicht, und ein fehlendes Datum käme als "01:00" bzw.
  // "1970-01-01" durch die Anzeige statt als Leerstring. Quelle solcher
  // Werte ist convertDateForIos(), das das MySQL-Nulldatum
  // "0000-00-00 00:00:00" bewusst zu null auflöst.
  if (date === null || date === undefined || date === '') return null
  const d = date instanceof Date ? date : new Date(date as any)
  if (!d || Number.isNaN(d.getTime())) return null

  const tz = isValidTimezone(timezone) ? timezone : DEFAULT_TIMEZONE
  const out: any = {}
  for (const part of partsFormatter(tz).formatToParts(d)) {
    if (part.type === 'literal') continue
    out[part.type] = part.value
  }
  return {
    year:    Number(out.year),
    month:   Number(out.month),
    day:     Number(out.day),
    // Manche Runtimes liefern für Mitternacht "24" statt "00".
    hour:    Number(out.hour) % 24,
    minute:  Number(out.minute),
    second:  Number(out.second),
    weekday: WEEKDAY_INDEX[out.weekday] ?? 0
  }
}

/**
 * Offset der Zeitzone zu UTC an DIESEM Zeitpunkt, in Millisekunden
 * (Europe/Berlin: +3600000 im Winter, +7200000 im Sommer).
 */
export function czOffsetMs (date: Date, timezone: string): number {
  const p = czParts(date, timezone)
  if (!p) return 0
  const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  // Sekundenbruchteile gehen in `asIfUTC` verloren und würden den Offset um
  // bis zu 999 ms verfälschen — vor der Differenz also abschneiden.
  return asIfUTC - (date.getTime() - date.getUTCMilliseconds())
}

/**
 * Umkehrung von `czParts`: aus einer Wandzeit im Studio wird ein Zeitpunkt.
 *
 * Zwei Durchläufe, weil der Offset selbst vom Ergebnis abhängt: der erste
 * Schätzwert kann auf der falschen Seite einer DST-Umstellung liegen. Nach dem
 * zweiten Durchlauf ist das Ergebnis stabil — außer für die eine Stunde, die es
 * lokal gar nicht gibt (Nacht der Vorstellung), dort landet man auf der Stunde
 * danach. Das ist das übliche und gewollte Verhalten.
 */
export function czMakeDate (
  year: number, month: number, day: number,
  hour = 0, minute = 0, second = 0,
  timezone: string = DEFAULT_TIMEZONE
): Date {
  const wall = Date.UTC(year, month - 1, day, hour, minute, second)
  let ts = wall - czOffsetMs(new Date(wall), timezone)
  ts = wall - czOffsetMs(new Date(ts), timezone)
  return new Date(ts)
}

/** `YYYY-MM-DD` des Zeitpunkts in Studiozeit — der Schlüssel jeder Tageszuordnung. */
export function czDateKey (date: Date | string | number | null | undefined, timezone: string): string {
  const p = czParts(date, timezone)
  if (!p) return ''
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/** Zeitpunkt von 00:00:00 Studiozeit des Tages, in dem `date` liegt. */
export function czStartOfDay (date: Date | string | number, timezone: string): Date | null {
  const p = czParts(date, timezone)
  if (!p) return null
  return czMakeDate(p.year, p.month, p.day, 0, 0, 0, timezone)
}

/**
 * Minuten seit Mitternacht Studiozeit — die Y-Position im Tages- und Wochenraster.
 *
 * Bewusst aus der Wandzeit gelesen und NICHT als `(start - tagesbeginn)`
 * gerechnet: an den beiden DST-Tagen im Jahr hat der Tag 23 bzw. 25 Stunden,
 * die vergangene Zeit weicht dann um 60 Minuten von der Wandzeit ab. Genau
 * dafür standen die `getTimezoneOffset()`-Vergleiche in den Manager-Views —
 * die sind mit dieser Funktion gegenstandslos.
 */
export function czMinutesOfDay (date: Date | string | number, timezone: string): number {
  const p = czParts(date, timezone)
  if (!p) return 0
  return p.hour * 60 + p.minute
}

/** Dauer zwischen zwei Zeitpunkten in Minuten. Zeitzonenunabhängig — echte Differenz. */
export function czDurationMinutes (start: Date | string | number, end: Date | string | number): number {
  const a = start instanceof Date ? start : new Date(start as any)
  const b = end   instanceof Date ? end   : new Date(end as any)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0
  return Math.round((b.getTime() - a.getTime()) / 60000)
}

/**
 * Formatiert in Studiozeit mit denselben Platzhaltern wie
 * `Date.prototype.formatDate` (core/helpers/date.js), damit Aufrufstellen 1:1
 * getauscht werden können: YYYY YY MMMM MM dd DD HH hh mm ss.
 */
export function czFormat (date: Date | string | number | null | undefined, timezone: string, format = 'DD.MM.YYYY'): string {
  const p = czParts(date, timezone)
  if (!p) return ''

  const pad = (n: number) => String(n).padStart(2, '0')
  let out = format

  if (out.indexOf('YYYY') > -1)      out = out.replace('YYYY', String(p.year))
  else if (out.indexOf('YY') > -1)   out = out.replace('YY', String(p.year).slice(2))

  if (out.indexOf('MMMM') > -1)      out = out.replace('MMMM', MONTHNAMES[p.month - 1])
  else if (out.indexOf('MM') > -1)   out = out.replace('MM', pad(p.month))

  out = out.replace('dd', DAYNAMES[p.weekday])
  out = out.replace('DD', pad(p.day))

  if (out.indexOf('HH') > -1) out = out.replace('HH', pad(p.hour))
  if (out.indexOf('hh') > -1) {
    let h = p.hour % 12
    if (h === 0) h = 12
    out = out.replace('hh', pad(h))
  }
  if (out.indexOf('mm') > -1) out = out.replace('mm', pad(p.minute))
  if (out.indexOf('ss') > -1) out = out.replace('ss', pad(p.second))

  return out
}

/**
 * Aktuelle Abweichung zweier Zeitzonen in Minuten, zum Zeitpunkt `at`.
 * 0 heißt: der Betrachter sieht gerade dieselbe Uhrzeit wie das Studio — auch
 * dann, wenn die Kennungen verschieden lauten (Europe/Berlin vs. Europe/Paris).
 * Genau deshalb wird der Hinweis am Offset festgemacht und nicht am Namen.
 */
export function czTimezoneDeltaMinutes (a: string, b: string, at: Date = new Date()): number {
  return Math.round((czOffsetMs(at, a) - czOffsetMs(at, b)) / 60000)
}

/** „+2 Std." / „−5:30 Std." — für den Hinweistext. */
export function formatOffsetDelta (minutes: number): string {
  const sign = minutes < 0 ? '−' : '+'
  const abs  = Math.abs(minutes)
  const h    = Math.floor(abs / 60)
  const m    = abs % 60
  return m === 0 ? `${sign}${h} Std.` : `${sign}${h}:${String(m).padStart(2, '0')} Std.`
}

/**
 * Aus einem Tagesschlüssel `YYYY-MM-DD` plus Minuten seit Mitternacht wird ein
 * Zeitpunkt. Das ist die Schreibrichtung des Kalenderrasters: der Nutzer zieht
 * einen Block auf Position N Minuten, gemeint ist N Minuten nach Mitternacht
 * STUDIOZEIT.
 *
 * Vorher stand an diesen Stellen `new Date("2026-08-20")` (= UTC-Mitternacht)
 * gefolgt von `.setHours(0,0,0)` (= Ortszeit-Mitternacht des Geräts) — zwei
 * verschiedene Zeitzonen in zwei aufeinanderfolgenden Zeilen. Solange beide
 * Berlin waren, hob sich das auf.
 *
 * Minuten über 1440 oder unter 0 sind erlaubt und laufen korrekt in den
 * Folge- bzw. Vortag über (`Date.UTC` normalisiert).
 */
export function czFromDateKey (dateKey: string, minutesOfDay: number, timezone: string): Date | null {
  if (typeof dateKey !== 'string') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateKey)
  if (!m) return null
  return czMakeDate(Number(m[1]), Number(m[2]), Number(m[3]), 0, Math.round(minutesOfDay), 0, timezone)
}

/** Liste wählbarer Zeitzonen für die Company-Einstellungen. */
export function supportedTimezones (): string[] {
  try {
    const all = (Intl as any).supportedValuesOf?.('timeZone') as string[] | undefined
    if (all && all.length) return all
  } catch { /* ältere Runtimes kennen supportedValuesOf nicht */ }
  return [
    'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/London',
    'Europe/Madrid', 'Europe/Rome', 'Europe/Warsaw', 'Europe/Athens',
    'Europe/Istanbul', 'Europe/Moscow', 'Atlantic/Canary', 'Asia/Dubai',
    'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
    'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Sao_Paulo', 'UTC'
  ]
}
